// CRM › Donations — in-app Awqaf donations (see AWQAF_DONATION_PLAN.md).
// Reads donations/{batchId} (written only by Cloud Functions; readable by
// superAdmin/finance/customerService per firestore.rules). Same layout as
// CRM › Orders: date-range filter, summary tiles, table, CSV export.
import moment from "moment";
import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from 'react-select';
import ReactTable from "react-table";
import { Badge, Button, Card, CardBody, Input, Row } from "reactstrap";
import { Colxx, Separator } from "../../../components/common/CustomBootstrap";
import DataTablePagination from "../../../components/DatatablePagination";
import ListPageHeading from "../../../containers/navs/ListPageHeading";
import { ORDER_COLLECTION } from "../../../config/env";
import { firestore } from '../../../helpers/Firebase';
import './crmstyles.css';
import DonationModal, { DONATION_STATUS, PILL } from './DonationModal';

const STATUS = DONATION_STATUS;
const statusOptions = Object.entries(STATUS).map(([value, s]) => ({ value, label: s.label }));

// Firestore `in` / getAll limits — fetch referenced docs in parallel batches
const fetchDocs = async (collection, ids) => {
  const out = {};
  const unique = [...new Set(ids.filter(Boolean))];
  await Promise.all(unique.map(async (id) => {
    try {
      const snap = await firestore().collection(collection).doc(id).get();
      if (snap.exists) out[id] = snap.data();
    } catch (e) { /* missing / no access — leave blank */ }
  }));
  return out;
};

const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

const Donations = () => {
  const [dateRange, setDateRange] = useState([moment().subtract(30, 'days').startOf('day').toDate(), new Date()]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async (range) => {
    if (!range?.[0] || !range?.[1]) return;
    setLoading(true);
    try {
      const snap = await firestore().collection('donations')
        .where('createdAt', '>=', moment(range[0]).startOf('day').valueOf())
        .where('createdAt', '<=', moment(range[1]).endOf('day').valueOf())
        .orderBy('createdAt', 'desc')
        .get();
      const donations = snap.docs.map((d) => ({ ...d.data(), batchId: d.id }));
      const [users, bookings] = await Promise.all([
        fetchDocs('users', donations.map((d) => d.uid)),
        fetchDocs(ORDER_COLLECTION, donations.map((d) => d.bookingId)),
      ]);
      setRows(donations.map((d) => ({
        ...d,
        customerName: users[d.uid]?.name || '',
        customerPhone: users[d.uid]?.phoneNumber || '',
        customerEmail: users[d.uid]?.email || '',
        orderNumber: bookings[d.bookingId]?.orderNumber || '',
        booking: bookings[d.bookingId] || null,
      })));
    } catch (e) {
      console.error('donations load failed', e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(dateRange); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const statuses = statusFilter.map((s) => s.value);
    return rows.filter((r) => (!statuses.length || statuses.includes(r.status))
      && (!q || [r.customerName, r.customerPhone, r.degTrn, r.batchId, String(r.orderNumber)]
        .some((v) => String(v || '').toLowerCase().includes(q))));
  }, [rows, statusFilter, search]);

  const summary = useMemo(() => {
    const paid = filtered.filter((r) => r.status === 'paid');
    return {
      total: paid.reduce((s, r) => s + (Number(r.amount) || 0), 0),
      paidCount: paid.length,
      donors: new Set(paid.map((r) => r.uid)).size,
      openCount: filtered.filter((r) => ['pending', 'creating'].includes(r.status)).length,
      failedCount: filtered.filter((r) => ['failed', 'error'].includes(r.status)).length,
    };
  }, [filtered]);

  const exportCSV = () => {
    const header = ['Date', 'Customer', 'Mobile', 'Amount (AED)', 'Status', 'DubaiPay reference', 'Order number', 'Batch ID', 'Paid at'];
    const lines = filtered.map((r) => [
      moment(r.createdAt).format('YYYY-MM-DD HH:mm'),
      r.customerName, r.customerPhone, Number(r.amount || 0).toFixed(2),
      STATUS[r.status]?.label || r.status, r.degTrn || '', r.orderNumber || '', r.batchId,
      r.paidAt ? moment(r.paidAt).format('YYYY-MM-DD HH:mm') : '',
    ].map(csvCell).join(','));
    const link = document.createElement('a');
    link.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent([header.map(csvCell).join(','), ...lines].join('\n'));
    link.download = `donations-${moment(dateRange[0]).format('YYYYMMDD')}-${moment(dateRange[1]).format('YYYYMMDD')}.csv`;
    link.click();
  };

  const columns = [
    { Header: 'Date', accessor: 'createdAt', Cell: ({ value }) => moment(value).format('DD/MM/YYYY hh:mm A') },
    {
      Header: 'Customer', accessor: 'customerName',
      Cell: ({ original }) => (
        <div>
          <div>{original.customerName || '—'}</div>
          <div className="text-muted text-small">{original.customerPhone}</div>
        </div>
      ),
    },
    { Header: 'Amount', accessor: 'amount', Cell: ({ value }) => `AED ${Number(value || 0).toFixed(2)}` },
    {
      Header: 'Status', accessor: 'status',
      Cell: ({ value }) => <Badge color={STATUS[value]?.color || 'secondary'} pill style={PILL}>{STATUS[value]?.label || value}</Badge>,
    },
    { Header: 'DubaiPay ref', accessor: 'degTrn', Cell: ({ value }) => value || '—' },
    { Header: 'Order #', accessor: 'orderNumber', Cell: ({ value }) => value || '—' },
    { Header: 'Batch ID', accessor: 'batchId', Cell: ({ value }) => <span className="text-small">{value}</span> },
    {
      Header: '', accessor: 'view', width: 80, sortable: false,
      Cell: ({ original }) => (
        <Button style={{ padding: '0.2rem 1rem' }} color="primary" size="sm" outline onClick={() => setSelected(original)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <Fragment>
      {loading && <div className="loading" />}
      <ListPageHeading heading="Donations" exportCSV={exportCSV} />

      <Row>
        <Colxx xxs="12">
          <Card className="mb-4 rounded">
            <CardBody>
              <Row>
                <Colxx xxs="12" md="4" className="mt-2">
                  <DatePicker
                    onChange={(dates) => { setDateRange(dates); if (dates[1]) load(dates); }}
                    startDate={dateRange[0]}
                    endDate={dateRange[1]}
                    selectsRange={true}
                    dateFormat={'dd/MM/yyyy'}
                    popperClassName="filter-datepicker"
                    popperPlacement="bottom-start"
                    showPopperArrow={false}
                  />
                </Colxx>
                <Colxx xxs="12" md="4" className="mt-2">
                  <Select
                    placeholder="Status"
                    isMulti
                    options={statusOptions}
                    value={statusFilter}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    onChange={(v) => setStatusFilter(v || [])}
                  />
                </Colxx>
                <Colxx xxs="12" md="4" className="mt-2">
                  <Input
                    style={{ borderRadius: '4px' }}
                    placeholder="Customer, mobile, DubaiPay ref or batch ID"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </Colxx>
              </Row>
            </CardBody>
          </Card>
        </Colxx>
      </Row>

      <Row>
        <Colxx xxs="12">
          <Card className="mb-4 rounded">
            <Row className="mt-4 pl-4 pr-4">
              {[
                ['Total donated', `AED ${summary.total.toFixed(2)}`],
                ['Paid donations', summary.paidCount],
                ['Donors', summary.donors],
                ['Pending / not completed', `${summary.openCount} / ${summary.failedCount}`],
              ].map(([title, value]) => (
                <Colxx key={title} xxs="6" md="3" className="d-flex flex-column justify-content-center align-items-center mb-2">
                  <div className="salesTitle">{title}</div>
                  <div className="salesValue">{value}</div>
                </Colxx>
              ))}
            </Row>
            <Separator className="mb-4 mt-4" />
            <CardBody style={{ paddingTop: 0 }}>
              <ReactTable
                data={filtered}
                columns={columns}
                defaultPageSize={50}
                minRows={0}
                pageSizeOptions={[20, 50, 100, 200]}
                showPageJump={false}
                PaginationComponent={DataTablePagination}
                showPageSizeOptions={true}
                noDataText="No donations in this period"
                getTrProps={(state, rowInfo) => ({
                  onClick: () => rowInfo && setSelected(rowInfo.original),
                  style: { cursor: rowInfo ? 'pointer' : 'default' },
                })}
              />
            </CardBody>
          </Card>
        </Colxx>
      </Row>
      <DonationModal donation={selected} isOpen={!!selected} dismiss={() => setSelected(null)} />
    </Fragment>
  );
};

export default Donations;
